import React, { useCallback, useEffect, useRef } from "react"
import { useRouter } from "next/router"

import { ConfirmationDialog } from "@/components/shared/alerts/ConfirmDialog"

import { useTestSetEditorContext } from "../contexts/TestSetEditorContext"

interface TestSetEditorNavigationGuardProps {
  children: React.ReactNode
}

export const TestSetEditorNavigationGuard: React.FC<
  TestSetEditorNavigationGuardProps
> = ({ children }) => {
  const router = useRouter()
  const { hasChanges, isExpectedBehaviorTestRunning, pendingChanges } =
    useTestSetEditorContext()
  const hasPendingChanges = (pendingChanges?.data?.length ?? 0) > 0
  const hasUnsavedChanges = hasChanges || hasPendingChanges
  const shouldBlock = hasUnsavedChanges || isExpectedBehaviorTestRunning
  const [showWarning, setShowWarning] = React.useState(false)
  const [nextUrl, setNextUrl] = React.useState<string | null>(null)
  const shouldBlockNavigation = useRef(true)
  const abortNavigationError = useRef(
    new Error("routeChange aborted by user confirmation"),
  )

  const handleRouteChangeStart = useCallback(
    (url: string) => {
      // Get current test set ID from the route
      const currentTestSetId = router.query.id as string
      const isLeavingEditor =
        currentTestSetId && !url.includes(`/test-sets/${currentTestSetId}`)

      // Don't block navigation from /new to a real test set (this is a create-save)
      const isNewToReal =
        currentTestSetId === "new" &&
        /\/test-sets\/[^/]+/.test(url) &&
        !url.includes("/test-sets/new") &&
        !url.endsWith("/test-sets/create")

      if (
        shouldBlockNavigation.current &&
        shouldBlock &&
        isLeavingEditor &&
        !isNewToReal
      ) {
        setNextUrl(url)
        setShowWarning(true)
        router.events.emit("routeChangeError")

        throw abortNavigationError.current
      }
    },
    [router.events, router.query.id, shouldBlock],
  )

  useEffect(() => {
    router.events.on("routeChangeStart", handleRouteChangeStart)

    // Handle browser back/forward buttons
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (shouldBlock) {
        e.preventDefault()
        e.returnValue = ""
        return ""
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload)

    return () => {
      router.events.off("routeChangeStart", handleRouteChangeStart)
      window.removeEventListener("beforeunload", handleBeforeUnload)
    }
  }, [router.events, handleRouteChangeStart, shouldBlock])

  const handleConfirmNavigation = () => {
    setShowWarning(false)
    if (nextUrl) {
      // Temporarily disable navigation blocking
      shouldBlockNavigation.current = false
      void router.push(nextUrl).then(() => {
        // Re-enable blocking after navigation completes
        shouldBlockNavigation.current = true
      })
      setNextUrl(null)
    }
  }

  const handleCancelNavigation = () => {
    setShowWarning(false)
    setNextUrl(null)
  }

  return (
    <>
      {children}
      <ConfirmationDialog
        showConfirmDialog={showWarning}
        title={
          isExpectedBehaviorTestRunning
            ? "A test is in progress"
            : "Leave Test Set Editor?"
        }
        description={
          isExpectedBehaviorTestRunning
            ? "Leaving now will stop tracking this test's results. Do you want to navigate away?"
            : "You have unsaved changes. Leaving the editor will cause you to lose this progress. Are you sure you want to continue?"
        }
        setShowConfirmDialog={setShowWarning}
        handleConfirmChange={handleConfirmNavigation}
        handleCancelChange={handleCancelNavigation}
        cancelLabel={
          isExpectedBehaviorTestRunning ? "Stay here" : "Keep editing"
        }
        confirmLabel={
          isExpectedBehaviorTestRunning ? "Navigate away" : "Discard changes"
        }
      />
    </>
  )
}
